/**
 * @description Renders the existing subjects in SECCON that would be affect by running the Personnel Sync
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import TablePagination from '@mui/material/TablePagination';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import useClientSidePagination from '@seccon/shared/ui/hooks/useClientSidePagination';
import NoResults from '@seccon/shared/ui/components/NoResults';
import isEmpty from 'lodash/isEmpty';

const SyncExistingSubjectsViewer = ({ isLoading, existingPersonnel, isSubmitting }) => {
  // Paginate the personnel list field array, so we don't display all the personnel all at once
  const {
    handleChangeRowsPerPage,
    handlePageChange,
    page,
    pageData,
    rowsPerPage,
    total
  } = useClientSidePagination({
    data: existingPersonnel,
    defaultPageSize: 50
  });

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
        <Skeleton animation='wave' variant='rectangular' height={40} width='100%' />
      </Stack>
    )
  }

  if (!existingPersonnel || isSubmitting) {
    return null
  }

  if (isEmpty(existingPersonnel)) {
    return (
      <NoResults Icon={WarningAmberIcon}>
        Based on the SSNs in the file, no personnel rows matched any records in Security Control.
      </NoResults>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant='h6'>
        Personnel to Update
      </Typography>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Facility Name</TableCell>
                <TableCell>SSN</TableCell>
                <TableCell>Facility CAGE Code</TableCell>
                <TableCell>View Personnel Record</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.map((row) => (
                <TableRow key={`row${ row.id }`}>
                  <TableCell>
                    {row.firstName}
                  </TableCell>
                  <TableCell>
                    {row.lastName}
                  </TableCell>
                  <TableCell>
                    {row.facilityName}
                  </TableCell>
                  <TableCell>
                    {row.ssn}
                  </TableCell>
                  <TableCell>
                    {row.facilityCageCode}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={`/personnel/show/${ row.id }`}
                      target='_blank'
                    >
                      Personnel Record
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={total}
          page={page}
          onPageChange={(_, value) => handlePageChange(value)}
          onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>
    </Stack>
  );
};

SyncExistingSubjectsViewer.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  existingPersonnel: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSubmitting: PropTypes.bool.isRequired
}

export default SyncExistingSubjectsViewer;
